import React from 'react';

// Shared read-only panel that renders a Swiggy/Zomato competitor-cost comparison
// (menu-price table + estimated all-in bill + offers). Used by the Orders detail
// popup AND the User-Insights cart view. `data` is the payload returned by
// /admin/competitor-intel/order-cost/:orderId or /cart-cost/:userId.
export default function CompetitorCostPanel({ data }) {
  if (!data) return null;
  if (!data.hasData) {
    return (
      <div style={{ fontSize: 13, color: 'var(--gray-600,#4b5563)' }}>
        {data.message || "This restaurant isn't mapped in Competitor Intel yet. Add its Swiggy/Zomato listing in /admin/competitor-intel and scrape it, then try again."}
      </div>
    );
  }
  const fmt = (n) => (n == null ? '—' : '₹' + n);
  const t = data.totals || {};
  const cell = (li, plat) => {
    const c = li[plat];
    if (!c) return <span style={{ color: '#9ca3af' }}>—</span>;
    return <span title={`${c.theirName || ''}${c.matchedBy ? ' · ' + c.matchedBy + ' match' : ''}`}>{fmt(c.lineTotal)}{c.approx ? <span style={{ color: '#b45309' }} title="variant not matched — competitor base price used"> ~</span> : null}</span>;
  };
  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#6b7280', borderBottom: '1px solid #e5e7eb' };
  const td = { padding: '6px 8px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9' };
  const tdR = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const covLine = (plat, label) => t[plat] && t[plat].hasData
    ? <div key={plat}>{label}: matched <b>{t[plat].matchedItems}/{data.totalItems}</b> items · ₹{t[plat].matchedSubtotal} of ₹{data.ourSubtotal} covered</div>
    : <div key={plat} style={{ color: '#9ca3af' }}>{label}: not mapped for this restaurant</div>;
  const est = (plat) => (t[plat] && t[plat].estimate) || null;
  const ob = data.ourBill;
  const eRow = (label, ourKey, compKey) => (
    <tr>
      <td style={td}>{label}</td>
      <td style={{ ...tdR, background: '#f8fafc' }}>{ob ? fmt(ob[ourKey]) : '—'}</td>
      <td style={tdR}>{est('swiggy') ? fmt(est('swiggy')[compKey]) : '—'}</td>
      <td style={tdR}>{est('zomato') ? fmt(est('zomato')[compKey]) : '—'}</td>
    </tr>
  );
  const scraped = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_) { return ''; } };
  return (
    <div>
      {/* Totals */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 120px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Our subtotal</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{fmt(data.ourSubtotal)}</div>
        </div>
        <div style={{ flex: '1 1 120px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 11, color: '#c2410c' }}>Swiggy (matched items)</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#c2410c' }}>{t.swiggy && t.swiggy.hasData ? fmt(t.swiggy.itemsTotal) : 'n/a'}</div>
        </div>
        <div style={{ flex: '1 1 120px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 11, color: '#b91c1c' }}>Zomato (matched items)</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#b91c1c' }}>{t.zomato && t.zomato.hasData ? fmt(t.zomato.itemsTotal) : 'n/a'}</div>
        </div>
      </div>
      {/* Per-item table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Item</th><th style={{ ...th, textAlign: 'center' }}>Qty</th>
            <th style={{ ...th, textAlign: 'right' }}>Our</th>
            <th style={{ ...th, textAlign: 'right' }}>Swiggy</th>
            <th style={{ ...th, textAlign: 'right' }}>Zomato</th>
          </tr></thead>
          <tbody>
            {(data.lineItems || []).map((li, i) => (
              <tr key={i}>
                <td style={td}>{li.name}{li.variant ? <span style={{ color: '#6b7280' }}> · {li.variant}</span> : null}</td>
                <td style={{ ...td, textAlign: 'center' }}>×{li.quantity}</td>
                <td style={tdR}>{fmt(li.ourLineTotal)}</td>
                <td style={tdR}>{cell(li, 'swiggy')}</td>
                <td style={tdR}>{cell(li, 'zomato')}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 800 }}>
              <td style={td}>Total (matched)</td><td style={td}></td>
              <td style={tdR}>{fmt(data.ourSubtotal)}</td>
              <td style={tdR}>{t.swiggy && t.swiggy.hasData ? fmt(t.swiggy.itemsTotal) : 'n/a'}</td>
              <td style={tdR}>{t.zomato && t.zomato.hasData ? fmt(t.zomato.itemsTotal) : 'n/a'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Coverage + freshness */}
      <div style={{ fontSize: 11.5, color: 'var(--gray-500,#6b7280)', marginTop: 10, lineHeight: 1.6 }}>
        {covLine('swiggy', 'Swiggy')}
        {covLine('zomato', 'Zomato')}
        <div style={{ marginTop: 4 }}>
          Item prices only (not the platform's final bill). <b>~</b> = variant not matched, base price used.
          {data.meta?.swiggy?.lastScrapedAt ? ` · Swiggy scraped ${scraped(data.meta.swiggy.lastScrapedAt)}` : ''}
          {data.meta?.zomato?.lastScrapedAt ? ` · Zomato scraped ${scraped(data.meta.zomato.lastScrapedAt)}` : ''}
        </div>
      </div>
      {/* Estimated all-in bill (items + fees) */}
      {(t.swiggy?.estimate || t.zomato?.estimate) && (
        <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 2 }}>Estimated all-in bill</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500,#6b7280)', marginBottom: 8 }}>
            Matched item prices + fees. {data.distanceKm > 0 ? `Delivery estimated from ${data.distanceKm} km.` : 'Delivery = base fee (no delivery distance for a cart).'} Excludes per-user coupons / membership waivers. Assumptions editable in Competitor Intel → Settings.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Component</th>
                <th style={{ ...th, textAlign: 'right', background: '#f8fafc' }}>Us{ob ? <span style={{ fontWeight: 400, color: '#94a3b8' }}> ({ob.isReal ? 'actual' : 'est.'})</span> : ''}</th>
                <th style={{ ...th, textAlign: 'right' }}>Swiggy</th>
                <th style={{ ...th, textAlign: 'right' }}>Zomato</th>
              </tr></thead>
              <tbody>
                {eRow('Items', 'items', 'itemsSubtotal')}
                {eRow('Packaging', 'packaging', 'packaging')}
                {eRow('Platform fee', 'platformFee', 'platformFee')}
                {eRow('Food GST', 'foodGst', 'foodGst')}
                {eRow('Delivery (est.)', 'delivery', 'delivery')}
                {ob && ob.discount > 0 && (
                  <tr style={{ color: '#166534' }}>
                    <td style={td}>Discount (ours)</td>
                    <td style={{ ...tdR, background: '#f8fafc' }}>−{fmt(ob.discount)}</td>
                    <td style={tdR}>—</td><td style={tdR}>—</td>
                  </tr>
                )}
                <tr style={{ fontWeight: 800 }}>
                  <td style={td}>{ob && ob.isReal ? 'Total' : 'Est. total'}</td>
                  <td style={{ ...tdR, background: '#eef2ff' }}>{ob ? fmt(ob.total) : '—'}</td>
                  <td style={tdR}>{est('swiggy') ? fmt(est('swiggy').total) : '—'}</td>
                  <td style={tdR}>{est('zomato') ? fmt(est('zomato').total) : '—'}</td>
                </tr>
                {(est('swiggy')?.bestOffer || est('zomato')?.bestOffer) && (
                  <tr style={{ color: '#166534', fontWeight: 700 }}>
                    <td style={td}>Best-case w/ offer</td>
                    <td style={{ ...tdR, background: '#f8fafc', color: '#374151', fontWeight: 400 }}>{ob ? fmt(ob.total) : '—'}</td>
                    <td style={tdR}>{est('swiggy') ? fmt(est('swiggy').bestOffer ? est('swiggy').bestCaseTotal : est('swiggy').total) : '—'}</td>
                    <td style={tdR}>{est('zomato') ? fmt(est('zomato').bestOffer ? est('zomato').bestCaseTotal : est('zomato').total) : '—'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {ob && (
            <div style={{ fontSize: 11, color: 'var(--gray-500,#6b7280)', marginTop: 6 }}>
              "Us" = your {ob.isReal ? 'actual bill for this order' : 'estimated bill (cart not checked out — delivery is a flat typical estimate since the distance is unknown)'}. Items compares the same dishes at your price vs theirs.
            </div>
          )}
          {(t.swiggy?.offers?.length || t.zomato?.offers?.length) ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Offers available</div>
              {['swiggy', 'zomato'].map((plat) => (t[plat]?.offers?.length ? (
                <div key={plat} style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'capitalize', color: '#6b7280' }}>{plat}:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {t[plat].offers.map((o, i) => (
                      <span key={i} title={o.conditional ? 'Card / bank / UPI specific — only with that payment method' : (o.code ? 'Code: ' + o.code : '')}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: o.conditional ? '#f3f4f6' : '#dcfce7', color: o.conditional ? '#6b7280' : '#166534', border: '1px solid ' + (o.conditional ? '#e5e7eb' : '#bbf7d0') }}>
                        {o.text}{o.conditional ? ' 💳' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null))}
              <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6 }}>💳 = card/bank/UPI-specific (needs that payment method — not applied to best-case). Best-case applies only the best generic offer to the item subtotal; real eligibility (min order / first order) may differ. Zomato loads offers per-user at checkout, so its list is usually empty here.</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
